import type { APIError } from '@vizit/api';
import { CollectionsDialog } from '@vizit/organisms';
import { useCollectionNewDialog, useProductCategoryList } from './collection-new-dialog.hooks';

interface CollectionNewDialogProps {
  /**
   * The ID of the portfolio to use for the collection
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * The ID of the organization to use for the collection
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The function to call when the collection is successfully created
   *
   * @param collectionId - The ID of the created collection
   */
  onSuccessfulCreate: (collectionId: string) => void;
  /**
   * The function to call when the authentication error occurs
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: APIError) => void;
  /**
   * Callback function when dialog open state changes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * CollectionNewDialog component
 * Provides a dialog interface for creating new collections with product category selection
 *
 * @param props - Component props
 * @returns CollectionNewDialog component
 *
 * @example
 * <CollectionNewDialog
 *   portfolioId="portfolio-123"
 *   organizationId="org-123"
 *   onSuccessfulCreate={(id) => console.log('Created:', id)}
 *   onAuthenticationError={(error) => console.error(error)}
 * />
 */
function CollectionNewDialog(props: CollectionNewDialogProps) {
  const {
    portfolioId,
    organizationId,
    useIcon = false,
    isOpen,
    hideTrigger = false,
    onSuccessfulCreate,
    onAuthenticationError,
    onOpenChange,
    ...other
  } = props;

  const productCategoryComboBoxProps = useProductCategoryList({
    organizationId,
    portfolioId,
    onAuthenticationError,
  });

  const { submissionErrorMessage, onSubmit } = useCollectionNewDialog({
    portfolioId,
    organizationId,
    onSuccessfulCreate,
    onAuthenticationError,
  });

  return (
    <CollectionsDialog
      submissionErrorMessage={submissionErrorMessage}
      productCategoryComboBoxProps={productCategoryComboBoxProps}
      useIcon={useIcon}
      isOpen={isOpen}
      hideTrigger={hideTrigger}
      onSubmit={onSubmit}
      onOpenChange={onOpenChange}
      data-testid="library-collections-create-collection"
      {...other}
    />
  );
}

export { CollectionNewDialog };
